import { useState, useCallback } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useGetTopicFitRecommendationsQuery, useApplyRecommendationsMutation } from '../../__generated__/chirp/com/hubspot/ai/engine/optimization/rpc/AiEngineOptimizationService.hooks';

/**
 * Custom hook for managing recommendation checkboxes with optimistic updates
 */
import { jsx as _jsx } from "react/jsx-runtime";
const useRecommendationCheckboxes = contentId => {
  const {
    data: fitRecommendations,
    refetch
  } = useGetTopicFitRecommendationsQuery({
    variables: {
      contentId
    }
  });
  const [localOverrides, setLocalOverrides] = useState(() => new Map());
  const [applyRecommendations] = useApplyRecommendationsMutation();
  const handleRecommendationChange = useCallback((recommendationId, currentApplied) => {
    const newApplied = !currentApplied;

    // Optimistic update
    setLocalOverrides(prev => new Map(prev.set(recommendationId, newApplied)));
    applyRecommendations({
      variables: {
        contentId,
        applyRecommendationIds: newApplied ? [recommendationId] : [],
        unapplyRecommendationIds: newApplied ? [] : [recommendationId]
      }
    }).then(() => {
      // Refetch to keep cache fresh for other components
      refetch().catch(() => {
        // Ignore refetch errors - not critical for UX
      });
    }).catch(error => {
      // Revert on error
      setLocalOverrides(prev => new Map(prev.set(recommendationId, currentApplied)));
      FloatingAlertStore.addAlert({
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "seo-tools-lib.contentRecommendations.error.updateRecommendationStatus"
        })
      });
      console.error('Failed to update recommendation:', error);
    });
  }, [applyRecommendations, contentId, refetch]);
  const getCurrentAppliedState = useCallback((recommendationId, serverApplied) => {
    var _localOverrides$get;
    return (_localOverrides$get = localOverrides.get(recommendationId)) !== null && _localOverrides$get !== void 0 ? _localOverrides$get : serverApplied;
  }, [localOverrides]);
  return {
    fitRecommendations,
    handleRecommendationChange,
    getCurrentAppliedState
  };
};
/**
 * Reusable component that displays AI search content recommendations as checkboxes with optimistic updates
 */
const AISearchContentRecommendations = ({
  contentId
}) => {
  const {
    fitRecommendations,
    handleRecommendationChange,
    getCurrentAppliedState
  } = useRecommendationCheckboxes(contentId);
  if (!(fitRecommendations !== null && fitRecommendations !== void 0 && fitRecommendations.recommendations)) {
    return null;
  }
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    gap: "400",
    marginBlockEnd: "400",
    children: fitRecommendations.recommendations.map(recommendation => {
      const currentAppliedState = getCurrentAppliedState(recommendation.id, recommendation.applied);
      return /*#__PURE__*/_jsx(Checkbox, {
        checked: currentAppliedState,
        onChange: () => handleRecommendationChange(recommendation.id, currentAppliedState // Use current state, not original server value
        ),
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          marginBlockStart: "-100",
          textDecoration: currentAppliedState ? 'line-through' : 'none',
          children: recommendation.recommendation
        })
      }, recommendation.id);
    })
  });
};
export default AISearchContentRecommendations;